import { voiceTrace } from './ariaVoiceTrace.js';
// One owner for work and playback across conversation and Assist.
export function createVoiceTurn({ onChange = () => {}, setTimer = setTimeout, clearTimer = clearTimeout, trace = voiceTrace.record } = {}) {
  let active = null;
  let serial = 0;
  const snapshot = () => ({ held: !!active, owner: active?.owner || null, id: active?.id || null,
    speaking: !!active && [...active.audio.values()].some((clip) => clip.speaking) });
  const emit = (event, turn, reason) => {
    try { trace(event, { id: turn.id, owner: turn.owner, work: turn.work, clips: turn.audio.size, reason }); } catch (_) {}
  };
  // ── THE DEADLINE FOLLOWS THE AUDIO, NOT THE WALL CLOCK (9/11) ────────────
  // Measured offline in shimmer-web/scripts/test-voice-floor.mjs against this
  // file and the real setMuted block in streamingTranscribe.js.
  //
  // Holding this turn shuts BOTH voice locks: App.jsx reads `held` as
  // floorHeldRef and drops any finished transcript, and its onChange mutes the
  // transcriber, which retires the socket so no audio even leaves the browser.
  // So a turn that is held is a turn in which she cannot hear. A single
  // wall-clock timer got that wrong at both ends:
  //
  //   too LATE  a leaked retain() (a reply path that never calls finish) held
  //             both locks for the whole timeout - 30 s in chat and 240 s in
  //             Assist - with nothing playing. Four minutes of being deaf.
  //   too EARLY the same timer cancelled a turn that was genuinely SPEAKING,
  //             and retire() pauses every clip, so an answer longer than 30 s
  //             was cut off mid-sentence.
  //
  // One rule fixes both: while a clip is actually playing the turn is alive and
  // is never cancelled; while it holds the locks with nothing playing it gives
  // them back at the idle ceiling, which is far below the wall clock.
  function armDeadline(turn) {
    if (!turn || active !== turn) return;
    clearTimer(turn.timer);
    const speaking = () => [...turn.audio.values()].some((clip) => clip.speaking);
    const live = speaking();
    turn.timer = setTimer(() => {
      if (active !== turn) return;
      if (speaking()) { armDeadline(turn); return; }   // still talking: alive
      cancel(turn, live ? 'deadline' : 'idle');
    }, live ? turn.timeoutMs : Math.min(turn.idleMs, turn.timeoutMs));
  }
  const publish = ({ rearm = false } = {}) => { if (rearm) armDeadline(active); onChange(snapshot()); };
  function retire(turn) {
    clearTimer(turn.timer);
    for (const [audio, clip] of turn.audio) {
      clip.cleanup();
      try { audio.pause(); } catch (_) {}
    }
    turn.audio.clear();
  }
  function cancel(turn, reason = 'cancelled') {
    if (active !== turn) return;
    emit('turn.release', turn, reason);
    active = null;
    retire(turn);
    publish();
  }
  function settle(turn, rearmIdle = false) {
    if (active !== turn) return;
    if (turn.work === 0 && turn.audio.size === 0) cancel(turn, 'settled');
    else publish({ rearm: rearmIdle });
  }
  // idleMs: the ceiling for a turn that is holding both locks with nothing
  // playing. Deliberately the same 12 s the transcriber's own mute watchdog
  // uses, doubled for headroom on a slow first token, so the two never drift
  // by minutes again. Callers can raise it; nothing may raise it past timeoutMs.
  function begin(owner, { timeoutMs = 30000, idleMs = 24000 } = {}) {
    const previous = active;
    const turn = { id: ++serial, owner, work: 1, audio: new Map(), timer: null, timeoutMs, idleMs };
    active = turn;
    if (previous) { emit('turn.replaced', previous); retire(previous); }
    emit('turn.begin', turn);
    let finished = false;
    const lease = {
      id: turn.id, owner,
      isCurrent: () => active === turn,
      finish() {
        if (finished || active !== turn) return;
        finished = true;
        turn.work--;
        emit('turn.work-finished', turn);
        settle(turn);
      },
      cancel: () => cancel(turn),
      retain() {
        if (active !== turn) return () => {};
        turn.work++;
        emit('turn.work-retained', turn);
        let done = false;
        return () => {
          if (done || active !== turn) return;
          done = true;
          turn.work--;
          emit('turn.work-finished', turn);
          settle(turn);
        };
      },
      play(audio) {
        if (active !== turn) return Promise.resolve();
        // Register before play(): work may finish before playback starts.
        let timer;
        let ended = false;
        const finish = (event) => {
          if (ended) return;
          ended = true;
          emit('audio.terminal', turn, event?.type || 'finished');
          clip.cleanup();
          turn.audio.delete(audio);
          settle(turn, true);
        };
        const playing = () => {
          if (active !== turn || ended) return;
          clip.speaking = true;
          emit('audio.playing', turn);
          publish({ rearm: true });
        };
        const events = [['playing', playing], ['ended', finish], ['error', finish], ['abort', finish], ['pause', finish]];
        const clip = { speaking: false, cleanup() {
          clearTimer(timer);
          for (const [name, fn] of events) audio.removeEventListener(name, fn);
        } };
        turn.audio.set(audio, clip);
        emit('audio.registered', turn);
        for (const [name, fn] of events) audio.addEventListener(name, fn);
        const safetyMs = Math.max(30000, (String(audio.src || '').length / 24000) * 1000 + 5000);
        timer = setTimer(() => { emit('audio.deadline', turn); try { audio.pause(); } catch (_) {} finish(); }, safetyMs);
        publish();
        try { return Promise.resolve(audio.play()).catch((error) => { finish({ type: 'play-rejected' }); throw error; }); }
        catch (error) { finish({ type: 'play-threw' }); return Promise.reject(error); }
      },
    };
    turn.lease = lease;
    // Arm once for the idle ceiling. Speech start/end transitions re-arm it;
    // no-op work publications must not keep a stalled turn alive forever.
    publish({ rearm: true });
    return lease;
  }
  return { begin, snapshot,
    current: (owner) => active && (!owner || active.owner === owner) ? active.lease : null,
    cancel: (owner) => { if (active && (!owner || active.owner === owner)) cancel(active); } };
}
